import { apiDelete, apiGet, apiPost, apiPut } from './client';

export type Role = 'OWNER' | 'ADMIN' | 'USER';

export type Permission =
  | 'VIEW_USERS'
  | 'CREATE_USERS'
  | 'EDIT_USERS'
  | 'ASSIGN_OWNER'
  | 'VIEW_PROJECT_DETAILS'
  | 'VIEW_ADMIN';

/** Where the server runs, from the top-level `environment:` key of its yaml (mirrors the server's `Environment`). */
export const Environment = {
  PRODUCTION: 'PRODUCTION',
  STAGING: 'STAGING',
  DEVELOPMENT: 'DEVELOPMENT',
  UNKNOWN: 'UNKNOWN',
} as const;
export type Environment = (typeof Environment)[keyof typeof Environment];

/** `GET /api/v1/me`: the caller's email, role and permissions plus the environment the server runs in. */
export interface MeResponse {
  email: string;
  role: Role;
  permissions: Permission[];
  environment: Environment;
}

/** Shape of a user row from `GET /api/v1/users` and the create/update/delete responses. */
export interface User {
  email: string;
  role: Role;
  subprojects: string[];
  department: string;
  isDeleted: boolean;
}

export interface RoleInfo {
  role: Role;
  permissions: Permission[];
}

export interface CreateUserRequest {
  email: string;
  role: Role;
  subprojects?: string[];
  department?: string;
}

export interface UpdateUserRequest {
  role: Role;
  subprojects?: string[];
  department?: string;
}

/**
 * One row from `GET /api/v1/users/{email}/history`. Each event records a single field change —
 * server returns a flat array sorted by timestamp ascending.
 */
export interface FormEvent {
  formId: string;
  /** Epoch milliseconds. */
  timestamp: number;
  field: string;
  value: string | null;
  user: string;
}

export function getMe(): Promise<MeResponse> {
  return apiGet<MeResponse>('/me');
}

export function getRoles(): Promise<RoleInfo[]> {
  return apiGet<RoleInfo[]>('/roles');
}

export function getDepartments(): Promise<string[]> {
  return apiGet<string[]>('/departments');
}

export function getUsers(includeDeleted = false): Promise<User[]> {
  // URLSearchParams over template literals per CODING_RULES — keeps query-string construction
  // consistent across the app even when the value is a fixed boolean today.
  const params = new URLSearchParams();
  if (includeDeleted) params.set('includeDeleted', 'true');
  const qs = params.toString();
  return apiGet<User[]>(qs ? `/users?${qs}` : '/users');
}

export function getUser(email: string): Promise<User> {
  return apiGet<User>(`/users/${encodeURIComponent(email)}`);
}

export function createUser(body: CreateUserRequest): Promise<User> {
  return apiPost<User, CreateUserRequest>('/users', body);
}

export function updateUser(email: string, body: UpdateUserRequest): Promise<User> {
  return apiPut<User, UpdateUserRequest>(`/users/${encodeURIComponent(email)}`, body);
}

export function deleteUser(email: string): Promise<User> {
  return apiDelete<User>(`/users/${encodeURIComponent(email)}`);
}

export function getUserHistory(email: string): Promise<FormEvent[]> {
  return apiGet<FormEvent[]>(`/users/${encodeURIComponent(email)}/history`);
}
